import React from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import {Box, Collapse, Stack, Typography} from '@mui/material';
import CoverageSourceMeter from './CoverageSourceMeter';
import {coverageScoreColor, coverageTrackColor} from '../coverageScoreColor';
import {COVERAGE_SOURCE} from '../coverageSourceTheme';

const SOURCE_ICON = {
    [COVERAGE_SOURCE.DATA.id]: FolderOutlinedIcon,
    [COVERAGE_SOURCE.ANALYTICS.id]: ArticleOutlinedIcon,
};

/**
 * One Data or Analytics pane: source label + meter + caption, expanding evidence.
 */
export default function CoverageSourcePane({
    source,
    coveragePct = null,
    caption,
    expanded = false,
    onToggle,
    testId,
    children,
}) {
    const Icon = SOURCE_ICON[source.id] || FolderOutlinedIcon;
    const canToggle = typeof onToggle === 'function';
    const hasEvidence = Boolean(children);
    const scored = coveragePct != null;
    const pctColor = scored ? coverageScoreColor(coveragePct) : source.color;
    const track = scored ? coverageTrackColor(coveragePct) : source.track;

    return (
        <Box
            data-testid={testId}
            onClick={canToggle ? onToggle : undefined}
            sx={{
                flex: 1,
                minWidth: 0,
                p: 1.5,
                pl: expanded ? 1.25 : 1.5,
                border: '1px solid',
                borderColor: expanded ? 'grey.300' : 'divider',
                borderLeft: expanded ? `3px solid ${source.accent || source.color}` : '1px solid',
                borderLeftColor: expanded ? (source.accent || source.color) : 'divider',
                borderRadius: 1.5,
                bgcolor: expanded ? 'grey.50' : 'background.paper',
                cursor: canToggle ? 'pointer' : 'default',
                transition: 'border-color 0.15s, background-color 0.15s',
            }}
        >
            <Stack spacing={0.75}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Icon sx={{fontSize: 16, color: source.color}} aria-hidden/>
                    <Typography
                        variant="caption"
                        sx={{
                            color: source.color,
                            fontWeight: 700,
                            letterSpacing: 0.7,
                            textTransform: 'uppercase',
                        }}
                    >
                        {source.label}
                    </Typography>
                    <Box flex={1}/>
                    {scored ? (
                        <Typography
                            variant="subtitle2"
                            fontFamily='"Roboto Mono", monospace'
                            fontWeight={700}
                            sx={{color: pctColor, lineHeight: 1, fontSize: '1.05rem'}}
                        >
                            {`${Math.round(coveragePct)}%`}
                        </Typography>
                    ) : (
                        <Typography
                            variant="caption"
                            fontFamily='"Roboto Mono", monospace'
                            fontWeight={500}
                            sx={{color: 'text.secondary', lineHeight: 1, fontSize: '0.75rem'}}
                        >
                            N/A
                        </Typography>
                    )}
                    {canToggle && hasEvidence ? (
                        expanded
                            ? <ExpandLessIcon fontSize="small" color="action"/>
                            : <ExpandMoreIcon fontSize="small" color="action"/>
                    ) : null}
                </Stack>
                <CoverageSourceMeter
                    pct={coveragePct}
                    color={pctColor}
                    track={track}
                    height={6}
                />
                {caption ? (
                    <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.4}}>
                        {caption}
                    </Typography>
                ) : null}
                {hasEvidence ? (
                    <Collapse in={expanded} unmountOnExit onClick={(event) => event.stopPropagation()}>
                        <Stack spacing={1} sx={{mt: 0.5}}>
                            {children}
                        </Stack>
                    </Collapse>
                ) : null}
            </Stack>
        </Box>
    );
}
